import { Combobox } from "@headlessui/react";
import {
  CaretDown,
  GridFour,
  ListBullets,
  MagnifyingGlass,
  UserCircle,
  XCircle,
} from "@phosphor-icons/react";
import {
  Badge,
  Button,
  DropdownMenu,
  Flex,
  Heading,
  SegmentedControl,
  Spinner,
  Text,
  TextField,
  Tooltip,
  IconButton,
} from "@radix-ui/themes";
import {
  useNavigate,
  useRouter,
  useRouterState,
  useSearch,
} from "@tanstack/react-router";
import cx from "classnames";
import { usePostHog } from "posthog-js/react";
import { useEffect, useMemo, useRef, useState } from "react";

import { Breadcrumbs } from "./Breadcrumbs";

import { useUI } from "../../../contexts/ui";
import { errorAnalyticsPayload } from "../../../utils/analytics";
import { canUploadTickets, isPunttGuest } from "../../../utils/auth";
import { batch } from "../../../utils/batch";
import { debounce } from "../../../utils/debounce";
import { useAppSelector } from "../../../utils/hooks";
import { errorMessage } from "../../../utils/http";
import {
  useEditProjectMutation,
  useUpdateFolderMutation,
} from "../../../utils/queries/projects";
import { ticketsRoute } from "../route";

type TicketsHeaderProps = {
  isPending: boolean;
  onSearch: (hasUserSearched: boolean) => void;
  onFilesUpload(): void;
  onFolderUpload(): void;
  onNewFolder(): void;

  // Breadcrumb actions
  onDeleteFolder(folderId: string): void;
  onRenameFolder(folderId: string): void;
  onShareFolder(folderId: string): void;
  onDownloadFolder(folderId: string): void;
};

export function TicketsHeader(props: TicketsHeaderProps) {
  const {
    isPending,
    onSearch,
    onFilesUpload,
    onNewFolder,
    onFolderUpload,
    onDeleteFolder,
    onShareFolder,
    onRenameFolder,
    onDownloadFolder,
  } = props;
  const { matches } = useRouterState();
  const posthog = usePostHog();

  const availableParticipants = useAppSelector(
    (state) => state.punttProjects.participants,
  );
  const isOnTicketsPage = matches.find(
    (m) => m.pathname.includes("/tickets") || m.pathname.includes("/ticket"),
  );
  const searchParams = useSearch({
    from: ticketsRoute.id,
  });
  const { folderId, participants, ticketTitle } = searchParams;

  // view can't be destructured because it's not in all branches of the union type
  const view = "view" in searchParams ? searchParams.view : undefined;
  const navigate = useNavigate({
    from: ticketsRoute.to,
  });
  const router = useRouter();

  const { notify } = useUI();

  const breadcrumbs = useAppSelector(
    (state) => state.punttProjects.breadcrumbs,
  );
  const updateFolderMutation = useUpdateFolderMutation();
  const editProjectMutation = useEditProjectMutation();
  useEffect(() => {
    document.title = `Puntt | ${breadcrumbs.at(-1)?.name ?? "Documents"}`;
    return () => {
      document.title = "Puntt";
    };
  }, [breadcrumbs]);

  const [searchState, setSearchState] = useState({
    ticketTitle: ticketTitle ?? "",
    participants: participants ?? [],
  });

  const onMoveToFolder = async (
    draggedIds: { _id: string; isFolder: boolean }[],
    targetFolderId?: string,
  ) => {
    const startTime = performance.now();
    const parentFolder = targetFolderId ?? false;

    try {
      await batch(
        draggedIds,
        1,
        async ([dragTargetId]) => {
          if (dragTargetId.isFolder) {
            await updateFolderMutation.mutateAsync({
              _id: dragTargetId._id,
              parentFolder,
            });
          } else {
            await editProjectMutation.mutateAsync({
              _id: dragTargetId._id,
              folder: parentFolder,
            });
          }
        },
        5,
      );
      router.invalidate();
      notify({
        title: "Moved to folder",
        message: `${draggedIds.length} item${draggedIds.length === 1 ? "" : "s"} moved`,
        variant: "success",
      });
      posthog.capture("tickets_dragged_to_sidebar", {
        folder_id: targetFolderId,
        dropped_ids: draggedIds,
        duration_ms: performance.now() - startTime,
        num_dropped: draggedIds.length,
      });
    } catch (error) {
      const message = await errorMessage(error);
      notify({
        title: "Error dragging to folder",
        message,
        variant: "error",
      });
      posthog.capture("tickets_dragged_to_sidebar_error", {
        folder_id: targetFolderId,
        num_dropped: draggedIds.length,
        dropped_ids: draggedIds,
        duration_ms: performance.now() - startTime,
        ...errorAnalyticsPayload(error),
      });
    }

    router.invalidate();
  };

  // useMemo here so that we don't keep redeclaring the timeout on component
  // re-render
  const handleSearch = useMemo(
    () =>
      debounce((newSearchState: typeof searchState) => {
        const hasUserSearched =
          newSearchState.ticketTitle.trim().length > 0 ||
          newSearchState.participants.length > 0;

        onSearch(hasUserSearched);
        navigate({
          to: ticketsRoute.to,
          search(prev) {
            return {
              ...prev,
              ticketTitle: newSearchState.ticketTitle,
              participants: newSearchState.participants,
            };
          },
          replace: true,
        });
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );
  const queryRef = useRef<HTMLInputElement>(null);
  const [participantsQuery, setParticipantsQuery] = useState("");

  const filteredParticipants =
    participantsQuery === ""
      ? availableParticipants
      : availableParticipants.filter((p) =>
          p.name
            ?.toLocaleLowerCase()
            .includes(participantsQuery.toLocaleLowerCase()),
        );

  return (
    <header
      className="flex w-full min-w-0 items-center justify-between gap-4"
      data-testid="tickets-header"
    >
      <Flex align="center" gap="2" className="min-w-0 overflow-hidden">
        <Heading as="h1" size="5" weight="bold" className="dali-font-national2">
          <span className="dali-font-national2">
            {/* Removed hardcoded Projects link - now handled by Breadcrumbs component */}
          </span>
        </Heading>
        <Breadcrumbs
          onDeleteFolder={() => onDeleteFolder(folderId!)}
          onShareFolder={() => onShareFolder(folderId!)}
          onRenameFolder={() => onRenameFolder(folderId!)}
          onDownloadFolder={() => onDownloadFolder(folderId!)}
          onMoveToFolder={(id, folder) => onMoveToFolder(id, folder)}
        />
      </Flex>

      <section className="flex min-w-0 shrink-0 items-center gap-2">
        <Spinner size="1" className={cx({ hidden: !isPending })} mr="2" />

        <TextField.Root
          placeholder={"Search Project Names"}
          data-testid="tickets-search-input"
          ref={queryRef}
          size="1"
          defaultValue={ticketTitle}
          onInput={({ target }) => {
            const newState = {
              ...searchState,
              ticketTitle: (target as HTMLInputElement).value,
            };
            setSearchState(newState);
            handleSearch(newState);
          }}
          className={cx("w-[173px] shrink-0")}
        >
          <TextField.Slot>
            <MagnifyingGlass />
          </TextField.Slot>
          {ticketTitle && (
            <TextField.Slot side="right">
              <IconButton
                size="1"
                variant="ghost"
                className="!p-1"
                data-testid="tickets-search-clear"
                onClick={() => {
                  if (queryRef.current) {
                    queryRef.current.value = "";
                  }
                  const newState = {
                    ...searchState,
                    ticketTitle: "",
                  };
                  setSearchState(newState);
                  handleSearch(newState);
                }}
              >
                <XCircle className="text-puntt-neutral-gray-9" />
              </IconButton>
            </TextField.Slot>
          )}
        </TextField.Root>

        <Combobox
          multiple
          value={participants ?? []}
          as="div"
          className={cx(
            "relative rounded-md focus-within:ring-2 focus-within:ring-puntt-blue-8 focus-within:ring-offset-[-1px]",
            { hidden: !isOnTicketsPage },
          )}
          disabled={isPending}
          data-testid="tickets-participants-combobox"
          onChange={(newParticipants) => {
            const newState = {
              ...searchState,
              participants: newParticipants,
            };
            setSearchState(newState);
            handleSearch(newState);
          }}
        >
          <Combobox.Button
            className="flex items-center gap-1 overflow-hidden rounded-md border border-puntt-neutral-gray-6 bg-base-white px-0.5 py-[3px]"
            data-testid="tickets-participants-button"
          >
            <UserCircle className="text-puntt-neutral-gray-9" />
            {Array.isArray(participants) && participants.length > 0 && (
              <Badge
                size="1"
                variant="surface"
                className="flex items-center gap-1"
              >
                {participants.length} selected
                <IconButton
                  size="1"
                  variant="ghost"
                  className="!h-auto !w-auto !p-0"
                  data-testid="tickets-participants-clear"
                  onClick={(e) => {
                    e.stopPropagation();
                    const newState = {
                      ...searchState,
                      participants: [],
                    };
                    setSearchState(newState);
                    handleSearch(newState);
                  }}
                >
                  <XCircle size={10} className="text-puntt-neutral-gray-9" />
                </IconButton>
              </Badge>
            )}
            <Combobox.Input
              onChange={({ target }) => setParticipantsQuery(target.value)}
              className="border-none text-xs outline-transparent"
              placeholder="Search Participants"
              data-testid="tickets-participants-input"
            />
            <CaretDown />
          </Combobox.Button>
          <Combobox.Options
            className="absolute top-full z-50 mt-0.5 grid max-h-72 w-full gap-1 overflow-auto rounded-md border border-puntt-neutral-gray-6 bg-base-white p-1 shadow-lg"
            data-testid="tickets-participants-options"
          >
            {filteredParticipants.map((p) => (
              <Combobox.Option
                key={p._id}
                value={p._id}
                disabled={isPending}
                data-testid={`tickets-participant-option-${p._id}`}
                className={({ active, selected, disabled }) =>
                  cx(
                    "flex cursor-pointer items-center justify-between rounded-lg px-2 py-0.5",
                    {
                      "opacity-50": disabled,
                      "bg-puntt-blue-5": active && selected && !disabled,
                      "bg-puntt-blue-9 text-base-white": selected || active,
                    },
                  )
                }
              >
                <Text size="1" className="truncate">
                  {p.name}
                </Text>
                {participants?.includes(p._id) && (
                  <XCircle className="text-base-white" />
                )}
              </Combobox.Option>
            ))}
          </Combobox.Options>
        </Combobox>

        <SegmentedControl.Root
          defaultValue={view ?? "grid"}
          data-testid="tickets-header-toggle"
          size="1"
          onValueChange={(value) => {
            posthog.capture("ticket_view_changed", {
              view: value,
            });

            localStorage.setItem(
              "layout",
              value as "list" | "grid" | "workflow",
            );

            navigate({
              to: ticketsRoute.to,
              search(prev) {
                return {
                  ...prev,
                  view: value as typeof view,
                };
              },
              replace: true,
            });
          }}
          className={cx({ hidden: !isOnTicketsPage })}
        >
          <SegmentedControl.Item
            value="list"
            data-testid="tickets-header-toggle-list"
          >
            <Tooltip content="List View">
              <ListBullets />
            </Tooltip>
          </SegmentedControl.Item>
          <SegmentedControl.Item
            value="grid"
            data-testid="tickets-header-toggle-grid"
          >
            <Tooltip content="Grid View">
              <GridFour />
            </Tooltip>
          </SegmentedControl.Item>
        </SegmentedControl.Root>

        <DropdownMenu.Root>
          <DropdownMenu.Trigger
            disabled={isPunttGuest() || !canUploadTickets()}
            className={cx({ hidden: !isOnTicketsPage })}
            data-testid="tickets-new-dropdown-trigger"
          >
            <Button size="1">
              New <CaretDown />
            </Button>
          </DropdownMenu.Trigger>

          <DropdownMenu.Content data-testid="tickets-new-dropdown-content">
            <DropdownMenu.Item
              onClick={onFilesUpload}
              data-testid="tickets-new-file-upload"
            >
              File Upload
            </DropdownMenu.Item>
            <DropdownMenu.Item onClick={onFolderUpload}>
              Folder Upload
            </DropdownMenu.Item>
            <DropdownMenu.Item
              onClick={onNewFolder}
              data-testid="tickets-new-folder"
            >
              New Folder
            </DropdownMenu.Item>
          </DropdownMenu.Content>
        </DropdownMenu.Root>
      </section>
    </header>
  );
}
